import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import sanitizeHtml from 'sanitize-html'

import Typography from '@segment/matcha/components/Typography'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { SectionWithAccordion } from 'views/Infrastructure/accordionData'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import type { PageConfig } from '@segment/contexts/page'
import { usePageConfig } from '@segment/contexts/page'

import AccordionSection from '../AccordionSection'

import DeliveryCentrifugeImage from './DeliveryCentrifugeImage'

const getData = (pageConfig: PageConfig) => ({
  anchorId: `centrifuge`,
  overline: `Delivery`,
  heading: `Centrifuge`,
  description: `APIs fail. At any given time, we see dozens of endpoints timing out, returning 5xx errors, or connection resets. We built <a href="${getAbsoluteUrl(
    `https://segment.com/blog/introducing-centrifuge/`,
    pageConfig
  )}">Centrifuge</a>, infrastructure to reliably retry and deliver messages even in cases of extreme failure. It brings up our message delivery by 1.5% on average.`,
  link1: getAbsoluteUrl(`https://segment.com/blog/introducing-centrifuge/`, pageConfig),
  link1Text: `Centrifuge: a reliable system for delivering billions of events per day (65 comments)`,
  link2: `https://github.com/segmentio/kafka-go`,
  link2Text: `segmentio/kafka-go (2,500 stars)`,
  accordionSection: SectionWithAccordion.CENTRIFUGE,
})

const DeliveryCentrifuge: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  const { anchorId, overline, heading, description, link1, link1Text, link2, link2Text, accordionSection } =
    getData(pageConfig)
  return (
    <Wrapper id={anchorId}>
      <SectionBase variant="2Col" copyVariant="wide">
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Overline>{overline}</CopyText.Overline>
            <CopyText.Heading>{heading}</CopyText.Heading>
            <CopyText.Description tag="div">
              <CopyTextDescriptionSection
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(description, { allowedTags: [`a`] }),
                }}
              />
              <CopyTextDescriptionSection>
                <Typography variant="pLarge">
                  <a href={link1}>{link1Text}</a>
                </Typography>
              </CopyTextDescriptionSection>
              <CopyTextDescriptionSection>
                <Typography variant="pLarge">
                  <a href={link2}>{link2Text}</a>
                </Typography>
              </CopyTextDescriptionSection>
              <AccordionSection section={accordionSection} />
            </CopyText.Description>
          </CopyTextBase>
        </Section.Copy>
        <StyledSectionContents>
          <DeliveryCentrifugeImage />
        </StyledSectionContents>
      </SectionBase>
    </Wrapper>
  )
}

const CopyTextDescriptionSection = styled.div`
  margin-bottom: 32px;
`

const Wrapper = styled.section`
  display: grid;
`

const StyledSectionContents = styled(Section.Contents)`
  margin: auto;
`

export default DeliveryCentrifuge
